import { LocaleLink as Link } from '../lib/locale-link.js';
import { useMarketingSeo } from '../lib/use-marketing-seo.js';
import { MarketingShell } from '../components/marketing-shell.js';
import { useTrialDays } from '../lib/use-trial-days.js';
import { useI18n } from '../lib/use-i18n.js';
import { RevealBlock } from '../components/reveal-block.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';

/**
 * /features — the full feature inventory grouped by audience:
 *   - For operators (queue, POS, customer book, loyalty, marketing, billing)
 *   - For customers (white-label PWA, queue tracking, loyalty)
 *   - For sales agents (referral channel, commission, payouts)
 *
 * Carries the same premium system as the home page (2026-08-25): ambient
 * radial wash on the hero, scroll-reveal on every group, staggered card
 * entrances and pointer lift. Before that this was flat typography, so a
 * prospect sold by the cinematic home clicked through to what looked like a
 * different product.
 *
 * Real product screenshots still land when there is a populated demo tenant to
 * capture — that is content, not code.
 */
export function FeaturesPage() {
  const brand = usePlatformBrand();
  const { t } = useI18n();
  useMarketingSeo({
    title: t('marketing.features.metaTitle', { brand: brand.name }),
    description: t('marketing.features.metaDescription', { brand: brand.name }),
  });
  const operatorFeatures: readonly FeatureCard[] = [
    { title: t('marketing.features.op1Title'), body: t('marketing.features.op1Body') },
    { title: t('marketing.features.op2Title'), body: t('marketing.features.op2Body') },
    { title: t('marketing.features.op3Title'), body: t('marketing.features.op3Body') },
    { title: t('marketing.features.op4Title'), body: t('marketing.features.op4Body') },
    { title: t('marketing.features.op5Title'), body: t('marketing.features.op5Body') },
    { title: t('marketing.features.op6Title'), body: t('marketing.features.op6Body') },
  ];
  const customerFeatures: readonly FeatureCard[] = [
    {
      title: t('marketing.features.cust1Title'),
      body: t('marketing.features.cust1Body', { brand: brand.name }),
    },
    { title: t('marketing.features.cust2Title'), body: t('marketing.features.cust2Body') },
    { title: t('marketing.features.cust3Title'), body: t('marketing.features.cust3Body') },
    { title: t('marketing.features.cust4Title'), body: t('marketing.features.cust4Body') },
  ];
  const agentFeatures: readonly FeatureCard[] = [
    { title: t('marketing.features.agent1Title'), body: t('marketing.features.agent1Body') },
    { title: t('marketing.features.agent2Title'), body: t('marketing.features.agent2Body') },
    { title: t('marketing.features.agent3Title'), body: t('marketing.features.agent3Body') },
    { title: t('marketing.features.agent4Title'), body: t('marketing.features.agent4Body') },
  ];
  return (
    <MarketingShell>
      <Hero />
      <FeatureGroup
        eyebrow={t('marketing.features.operatorEyebrow')}
        title={t('marketing.features.operatorTitle')}
        groups={operatorFeatures}
      />
      <FeatureGroup
        eyebrow={t('marketing.features.customerEyebrow')}
        title={t('marketing.features.customerTitle')}
        groups={customerFeatures}
        muted
      />
      <FeatureGroup
        eyebrow={t('marketing.features.agentEyebrow')}
        title={t('marketing.features.agentTitle')}
        groups={agentFeatures}
      />
      <BottomCta />
    </MarketingShell>
  );
}

function Hero() {
  const { t } = useI18n();
  return (
    <section
      style={{
        position: 'relative',
        padding: 'var(--space-12) var(--space-6) var(--space-8) var(--space-6)',
        textAlign: 'center',
        // The ambient wash the home hero uses, at a lower amplitude: this is a
        // secondary page and should feel like the same site without competing
        // with the page that has the 3D hero.
        background:
          'radial-gradient(120% 90% at 50% -10%, rgba(var(--color-primary-rgb), 0.10), transparent 62%)',
      }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <p style={eyebrowStyle}>{t('marketing.features.eyebrow')}</p>
        <h1
          style={{
            fontSize: 'var(--text-display)',
            lineHeight: 1.05,
            fontWeight: 700,
            margin: 'var(--space-2) 0 0 0',
            letterSpacing: '-0.02em',
          }}
        >
          {t('marketing.features.heroTitle')}
        </h1>
        <p
          style={{
            margin: 'var(--space-4) auto 0 auto',
            maxWidth: 560,
            fontSize: 'var(--text-body)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {t('marketing.features.heroSubtitle')}
        </p>
      </div>
    </section>
  );
}

interface FeatureCard {
  readonly title: string;
  readonly body: string;
}

function FeatureGroup({
  eyebrow,
  title,
  groups,
  muted = false,
}: {
  readonly eyebrow: string;
  readonly title: string;
  readonly groups: readonly FeatureCard[];
  readonly muted?: boolean;
}) {
  return (
    <section
      style={{
        padding: 'var(--space-12) var(--space-6)',
        background: muted ? 'var(--tint-subtle)' : 'transparent',
      }}
    >
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        {/* The same entrance the home page uses. This page was the plainest
            surface on the site: a prospect who was sold by the cinematic home
            clicked through to flat typography, which reads as two different
            products rather than as one. */}
        <RevealBlock>
          <p style={eyebrowStyle}>{eyebrow}</p>
          <h2
            style={{
              margin: 'var(--space-2) 0 0 0',
              fontSize: 'var(--text-h1)',
              fontWeight: 600,
              letterSpacing: '-0.01em',
            }}
          >
            {title}
          </h2>
        </RevealBlock>
        <RevealBlock
          style={{
            marginTop: 'var(--space-8)',
            display: 'grid',
            gap: 'var(--space-4)',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
          }}
        >
          {groups.map((f, i) => (
            <article
              key={f.title}
              data-stagger
              className="lift"
              // Per-item delay, inline because it is per-item: the CSS owns the
              // animation, this only says when each card joins it.
              style={{
                animationDelay: `${i * 70}ms`,
                padding: 'var(--space-5)',
                borderRadius: 'var(--radius-lg)',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                boxShadow: 'var(--shadow-1)',
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 'var(--text-body)',
                  fontWeight: 600,
                  lineHeight: 1.3,
                }}
              >
                {f.title}
              </h3>
              <p
                style={{
                  margin: 'var(--space-2) 0 0 0',
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                  lineHeight: 1.5,
                }}
              >
                {f.body}
              </p>
            </article>
          ))}
        </RevealBlock>
      </div>
    </section>
  );
}

function BottomCta() {
  const { t } = useI18n();
  const trialDays = useTrialDays();
  return (
    <section
      style={{
        padding: 'var(--space-12) var(--space-6) var(--space-16) var(--space-6)',
        textAlign: 'center',
      }}
    >
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--text-h1)',
            fontWeight: 600,
            letterSpacing: '-0.01em',
          }}
        >
          {t('marketing.features.bottomTitle')}
        </h2>
        <p
          style={{
            margin: 'var(--space-3) 0 0 0',
            fontSize: 'var(--text-body)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {t('marketing.features.bottomBody', { days: trialDays })}
        </p>
        <Link
          to="/signup"
          style={{
            display: 'inline-block',
            marginTop: 'var(--space-6)',
            padding: '14px 28px',
            borderRadius: 'var(--radius-pill)',
            background: 'var(--color-primary)',
            color: 'var(--color-primary-fg)',
            fontWeight: 600,
            textDecoration: 'none',
            boxShadow: 'var(--shadow-2)',
          }}
        >
          {t('marketing.cta.startTrial')}
        </Link>
      </div>
    </section>
  );
}

const eyebrowStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-primary)',
  letterSpacing: 3,
  textTransform: 'uppercase',
  fontWeight: 600,
  margin: 0,
};
